import { createClient } from "@supabase/supabase-js";
import { Database } from "./supabaseTypesImport";
import { Logger } from "../logger/Logger";

const RATE_LIMIT_CONFIG = {
	REQUESTS_PER_MINUTE: 800,
} as const;

type RecentCall = {
	endpoint: string;
	method: string;
	query: string;
	timestamp: number;
};

type RateLimitState = {
	blocked: boolean;
	timestamps: number[];
	recentCalls: RecentCall[];
};

const state: RateLimitState = {
	blocked: false,
	timestamps: [],
	recentCalls: [],
};

// Scoped bypass for known-bursty flows (e.g. the DLXP migration upload, which
// can fire thousands of storage + insert requests in a row). Callers must
// flip it back off in a finally{} so the guard is never disabled
// permanently. Auth requests are already exempt above.
let bypassEnabled = false;
export function setRateLimitBypass(enabled: boolean): void {
	bypassEnabled = enabled;
	if (enabled) {
		state.blocked = false;
		state.timestamps = [];
	}
}

function setBlocked(): void {
	state.blocked = true;
}

function isBlocked(): boolean {
	return state.blocked;
}

function trackRequest(url: string, options?: RequestInit): void {
	const now = Date.now();
	state.timestamps.push(now);

	const method = options?.method || "GET";

	try {
		const parsed = new URL(url);
		const endpoint = parsed.pathname
			.replace("/rest/v1/", "")
			.replace("/rpc/", "rpc/");
		const query = decodeURIComponent(parsed.search.replace("?", "")).replace(
			/&/g,
			", "
		);
		state.recentCalls.push({ endpoint, method, query, timestamp: now });
	} catch {
		state.recentCalls.push({ endpoint: url, method, query: "", timestamp: now });
	}

	if (state.recentCalls.length > 20) {
		state.recentCalls.shift();
	}

	const oneMinuteAgo = now - 60_000;
	state.timestamps = state.timestamps.filter((t) => t > oneMinuteAgo);
}

function getRecentCallsForSupport(): string {
	const count = state.timestamps.length;
	const limit = `Limit: ${RATE_LIMIT_CONFIG.REQUESTS_PER_MINUTE}/min`;
	const current = `Aktuell: ${count}/min`;
	const page = `Seite: ${window.location.pathname}`;

	if (state.recentCalls.length === 0) {
		return `${limit}\n${current}\n${page}\n\nKeine Anfragen aufgezeichnet`;
	}

	const lines = state.recentCalls.map((call) => {
		const date = new Date(call.timestamp);
		const time = date.toLocaleTimeString("de-DE");
		const queryPart = call.query ? `\n   → ${call.query}` : "";
		return `${time} ${call.method} ${call.endpoint}${queryPart}`;
	});

	return `${limit}\n${current}\n${page}\n\nLetzte ${state.recentCalls.length} DB-Anfragen:\n${lines.join("\n\n")}`;
}

export type RateLimitDialogData = {
	open: boolean;
	recentCalls: string;
};

let dialogState: RateLimitDialogData = {
	open: false,
	recentCalls: "",
};

const dialogListeners = new Set<() => void>();

function notifyDialogListeners(): void {
	dialogListeners.forEach((listener) => listener());
}

export function subscribeToRateLimitDialog(listener: () => void): () => void {
	dialogListeners.add(listener);
	return () => dialogListeners.delete(listener);
}

export function getRateLimitDialogSnapshot(): RateLimitDialogData {
	return dialogState;
}

function showRateLimitDialog(): void {
	const recentCalls = getRecentCallsForSupport();
	dialogState = {
		open: true,
		recentCalls,
	};
	notifyDialogListeners();
}

// --- Connection / auth overlay (mirrors the rate-limit dialog singleton above) ---
// Surfaces a full-screen overlay when the session expired (HTTP 401 / PGRST301) or
// the backend is unreachable, so the user gets an explanation instead of a blank page.
export type ConnectionOverlayStatus =
	| "session_expired"
	| "connection_error"
	| null;

type ConnectionOverlayData = { status: ConnectionOverlayStatus };

let connectionOverlayState: ConnectionOverlayData = { status: null };
const connectionOverlayListeners = new Set<() => void>();

// Set while the user intentionally signs out, so the resulting SIGNED_OUT event
// and any in-flight 401s don't flash the "Sitzung abgelaufen" overlay on logout.
let intentionalSignOut = false;
export function setIntentionalSignOut(value: boolean): void {
	intentionalSignOut = value;
}

export function subscribeToConnectionOverlay(listener: () => void): () => void {
	connectionOverlayListeners.add(listener);
	return () => connectionOverlayListeners.delete(listener);
}

export function getConnectionOverlaySnapshot(): ConnectionOverlayData {
	return connectionOverlayState;
}

function setConnectionOverlay(status: ConnectionOverlayStatus): void {
	if (connectionOverlayState.status === status) return;
	connectionOverlayState = { status };
	connectionOverlayListeners.forEach((listener) => listener());
}

// Periodic cleanup of old timestamps
setInterval(() => {
	const now = Date.now();
	const oneMinuteAgo = now - 60_000;
	state.timestamps = state.timestamps.filter((t) => t > oneMinuteAgo);
}, 60_000);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function guardedFetch(url: any, options: any) {
	// Always allow auth requests (login, token refresh, etc.)
	if (typeof url === "string" && url.includes("/auth/v1/")) {
		return fetch(url, options);
	}

	// Scoped bypass — see setRateLimitBypass.
	if (bypassEnabled) {
		return fetch(url, options);
	}

	// If blocked, reject and show dialog
	if (isBlocked()) {
		showRateLimitDialog();
		return Promise.reject(
			new Error("[Rate Limit] Blockiert. Bitte Seite neu laden.")
		);
	}

	trackRequest(url, options);

	const count = state.timestamps.length;

	Logger.log(
		`[Rate Limit] ${count}/${RATE_LIMIT_CONFIG.REQUESTS_PER_MINUTE}/min`
	);

	if (count > RATE_LIMIT_CONFIG.REQUESTS_PER_MINUTE) {
		setBlocked();
		showRateLimitDialog();
		return Promise.reject(
			new Error("[Rate Limit] Blockiert. Bitte Seite neu laden.")
		);
	}

	return fetch(url, options)
		.then((response) => {
			Logger.log("[Supabase Logs]", url, options, response);
			if (!response.ok) {
				Logger.warn("[Supabase Logs] Response error:", response.statusText);
			}
			// A 401 on a non-auth request means the JWT is expired/invalid (auth
			// requests are exempted above), so the session is no longer usable.
			if (response.status === 401 && !intentionalSignOut) {
				setConnectionOverlay("session_expired");
			}
			// A successful response means connectivity is back — clear a prior
			// connection error so the overlay self-heals without a manual reload.
			if (
				response.ok &&
				connectionOverlayState.status === "connection_error"
			) {
				setConnectionOverlay(null);
			}
			return response;
		})
		.catch((error) => {
			Logger.error("[Supabase Logs] Fetch error:", error);
			// A rejected fetch (not an aborted request) means the backend is
			// unreachable — surface it instead of letting pages render blank.
			if (error?.name !== "AbortError" && !intentionalSignOut) {
				setConnectionOverlay("connection_error");
			}
			throw error;
		});
}

function getSupabaseUrl(): string {
	const raw = import.meta.env.VITE_SUPABASE_PROJECT_URL;
	const isLocalUrl = raw.substring(0, 4) === "http";
	const url = isLocalUrl ? raw : `https://${raw}.supabase.co`;
	if (import.meta.env.DEV && isLocalUrl) {
		const parsed = new URL(url);
		parsed.hostname = window.location.hostname;
		return parsed.toString().replace(/\/$/, "");
	}
	return url;
}

const supabaseUrl: string = getSupabaseUrl();
const supabaseAnonKey: string = import.meta.env
	.VITE_SUPABASE_PUBLIC_API_ANON_KEY;

const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
	global: { fetch: guardedFetch.bind(globalThis) },
});

// Backstop for token-refresh failures on an idle tab: those sign the user out
// without ever hitting guardedFetch (auth requests are exempt). A successful
// sign-in / refresh clears any overlay so it self-heals.
supabase.auth.onAuthStateChange((event, session) => {
	if (event === "SIGNED_OUT") {
		if (!intentionalSignOut) setConnectionOverlay("session_expired");
	} else if (session) {
		setConnectionOverlay(null);
	}
});

export { supabase };
